import { ComponentProps, forwardRef } from "react";

import { Plan, PurchasablePlan, useWorkspaceStatsQuery } from "@/apollo/types";
import { Alert, AlertIcon, AlertTitle } from "@/components/elements/Alert";
import AppearTransition from "@/components/elements/AppearTransition";
import { Button } from "@/components/elements/Button";
import { ChangePlanDialog } from "@/features/billing";
import {
  useCurrentPlan,
  useWeldPlanLimits,
  useWeldPlans,
} from "@/features/subscription";
import cn from "@/helpers/classNames";
import { useDisclosure } from "@/hooks/useDisclosure";
import { LinkWithSlug } from "@/router";

const DefaultAlert = forwardRef<
  HTMLDivElement,
  {
    plan: Plan;
    targetPlan?: PurchasablePlan;
    onClickConnections: () => void;
  } & ComponentProps<typeof Alert>
>((props, ref) => {
  const {
    plan: _plan,
    targetPlan: _targetPlan,
    onClickConnections,
    ...rest
  } = props;
  const disclosure = useDisclosure();
  return (
    <Alert
      ref={ref}
      status="info"
      {...rest}
      className={cn("flex-col sm:flex-row", rest.className)}
    >
      <div className="flex items-center">
        <AlertIcon />
        <div>
          <AlertTitle className="mb-1">Connector Limit Reached</AlertTitle>
          Your plan limits how many <strong>unique connector types</strong> you
          can use. You can still add more{" "}
          <strong>instances of connectors you already have</strong>, but adding
          new types requires an upgrade.
          <div className="mt-2 flex divide-x divide-black/40">
            <div className="pr-2">
              <Button
                as={LinkWithSlug}
                to="/settings/connections"
                size="xs"
                variant="link"
                className="font-medium"
                onClick={() => onClickConnections()}
              >
                Manage Connections
              </Button>
            </div>
            <div className="pl-2">
              <Button
                as="a"
                href="https://weld.app/pricing"
                target="_blank"
                size="xs"
                variant="link"
                className="font-medium"
              >
                Learn more about our plans
              </Button>
            </div>
          </div>
          <ChangePlanDialog {...disclosure} />
        </div>
      </div>
      <div className="mx-4 flex grow justify-end">
        <Button
          variant="ghost"
          colorScheme="primary"
          onClick={() => disclosure.onOpen()}
        >
          Upgrade
        </Button>
        <ChangePlanDialog {...disclosure} />
      </div>
    </Alert>
  );
});

export const ConnectorsLimitAlert = forwardRef<
  HTMLDivElement,
  {
    onClickConnections?: () => void;
  } & ComponentProps<typeof Alert>
>((props, ref) => {
  const plan = useCurrentPlan();
  const plans = useWeldPlans();

  const { data: workspaceStatsData } = useWorkspaceStatsQuery({
    fetchPolicy: "cache-and-network",
  });
  const { isConnectorsLimitReached } = useWeldPlanLimits();
  if (!isConnectorsLimitReached) {
    return null;
  }

  const targetPlan = plans.getApplicablePlan({
    connectors: (workspaceStatsData?.workspaceStats.numConnections ?? 0) + 1,
  });

  return (
    <AppearTransition>
      <DefaultAlert
        ref={ref}
        plan={plan}
        targetPlan={targetPlan?.plan}
        onClickConnections={() => props.onClickConnections?.()}
        {...props}
      />
    </AppearTransition>
  );
});
